
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface HeaderProps {
  btcPrice: string;
}

export default function Header({ btcPrice }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'zh' : 'en';
    i18n.changeLanguage(newLang);
  };

  return (
    <nav className="sticky top-0 w-full bg-[#f8f8f0]/90 backdrop-blur-md z-50 px-2">
      <div className="max-w-[100vw] mx-auto px-2 py-1">
        <div className="flex items-center justify-between">
          <div className="flex flex-col lg:gap-1 lg:flex-row">
            <div className="text-orange-500 text-xl font-bold md:text-2xl justify-left py-0">
              <a href="/" className="cursor-pointer">
                <span className="text-blue-900">REAL</span>RISES
              </a>
            </div>
          </div>
          
          <div className="hidden md:flex space-x-4 items-center grow justify-end mr-2">
            <div className="mr-2 pr-4 flex gap-2">
              <div className="flex flex-row items-center bg-blue-900/10 tracking-normal px-3 py-1 rounded-3xl text-sm w-fit">
                <div>
                  <a href="https://bitcoin.zorinaq.com/price/" className="cursor-pointer">
                    <div className="text-nowrap text-blue-900">
                      <span className="font-light">BTC</span> 
                      <span className="font-extrabold tracking-wide">{btcPrice}</span>
                    </div>
                  </a>
                </div>
              </div>
              <div className="flex flex-row items-center bg-blue-900/10 tracking-normal px-3 py-1 rounded-3xl text-sm w-fit">
                <div>
                  <a href="https://finance.yahoo.com/quote/QQQ" className="cursor-pointer" target="_blank" rel="noopener noreferrer">
                    <div className="text-nowrap text-blue-900">
                      <span className="font-light">QQQ</span> 
                      <span className="font-extrabold tracking-wide">$520.45</span>
                    </div>
                  </a>
                </div>
              </div>
            </div>
            <button 
              onClick={toggleLanguage}
              className="tracking-normal text-blue-900 hover:text-orange-500 transition-colors text-nowrap font-medium cursor-pointer text-sm"
            >
              {i18n.language === 'en' ? '中文' : 'EN'}
            </button>
            <button 
              onClick={() => scrollToSection('how-it-works')}
              className="tracking-normal text-blue-900 hover:text-orange-500 transition-colors text-nowrap font-medium cursor-pointer"
            >
              {t('navigation.howItWorks')}
            </button>
            <button 
              onClick={() => scrollToSection('calculator-section')}
              className="tracking-normal text-blue-900 hover[text-orange-500] transition-colors text-nowrap font-medium cursor-pointer"
            >
              {t('navigation.calculator')}
            </button>
            <button 
              onClick={() => scrollToSection('faq')}
              className="tracking-normal text-blue-900 hover[text-orange-500] transition-colors text-nowrap font-medium cursor-pointer"
            >
              {t('navigation.faq')}
            </button>
            <button 
              onClick={() => scrollToSection('about')}
              className="tracking-normal text-blue-900 hover:text-orange-500 transition-colors text-nowrap font-medium cursor-pointer"
            >
              {t('navigation.aboutUs')}
            </button>
            <button 
              onClick={() => scrollToSection('prequalify-section')}
              className="shadow-[0px_1px_1.5px_0px_rgba(0,0,0,0.1)] border-t-1 border-t-white/20 border-b-1 border-b-black/10 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-light transition-all disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none bg-gradient-to-b from-orange-500 to-orange-600 text-white hover:text-gray-100 active:from-orange-600 active:to-orange-500 h-9 px-4 py-5 cursor-pointer"
            >
              {t('navigation.getStarted')}
            </button>
          </div>
          
          <div className="md:hidden flex items-center">
            <button 
              onClick={toggleLanguage}
              className="tracking-normal text-blue-900 hover[text-orange-500] transition-colors text-nowrap font-medium cursor-pointer text-sm mr-2"
            >
              {i18n.language === 'en' ? '中文' : 'EN'}
            </button>
            <button 
              onClick={() => scrollToSection('prequalify-section')}
              className="shadow-[0px_1px_1.5px_0px_rgba(0,0,0,0.1)] border-t-1 border-t-white/20 border-b-1 border-b-black/10 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-light transition-all bg-gradient-to-b from-orange-500 to-orange-600 text-white hover:text-gray-100 h-9 px-3 py-1 text-sm cursor-pointer"
            >
              {t('navigation.getStarted')}
            </button>
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-black p-2 h-10 w-10 flex items-center justify-center cursor-pointer"
            >
              <i className={isMenuOpen ? "ri-close-line" : "ri-menu-line"}></i>
            </button>
          </div>
        </div>
        
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-200">
            <div className="flex flex-col space-y-2 pt-4">
              <button 
                onClick={() => {scrollToSection('how-it-works'); setIsMenuOpen(false);}}
                className="text-blue-900 hover[text-orange-500] transition-colors font-medium text-left cursor-pointer"
              >
                {t('navigation.howItWorks')}
              </button>
              <button 
                onClick={() => {scrollToSection('calculator-section'); setIsMenuOpen(false);}}
                className="text-blue-900 hover[text-orange-500] transition-colors font-medium text-left cursor-pointer"
              >
                {t('navigation.calculator')}
              </button>
              <button 
                onClick={() => {scrollToSection('faq'); setIsMenuOpen(false);}}
                className="text-blue-900 hover[text-orange-500] transition-colors font-medium text-left cursor-pointer"
              >
                {t('navigation.faq')}
              </button>
              <button 
                onClick={() => {scrollToSection('about'); setIsMenuOpen(false);}}
                className="text-blue-900 hover:text-orange-500 transition-colors font-medium text-left cursor-pointer"
              >
                {t('navigation.aboutUs')}
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
